import { DependencyMapReference, DependencyMapReferenceParent } from './types';
import { localeCompareNumericLowercase, type Locale } from '@ardoq/locale';
import { type ContextSort } from '@ardoq/common-helpers';
import { compareComponentIds } from './compareComponentIds';
import type { EnhancedScopeData } from '@ardoq/data-model';
import type { ArdoqId } from '@ardoq/api-types';

const rootParent = (
  reference: DependencyMapReference | DependencyMapReferenceParent
): DependencyMapReference | DependencyMapReferenceParent =>
  reference.parent ? rootParent(reference.parent) : reference;

const getSimpleReferenceComparator =
  (
    referenceTypeNames: Map<string, string>,
    sort: ContextSort,
    locale: Locale,
    scopeData: EnhancedScopeData,
    workspaceIds: ArdoqId[]
  ) =>
  (
    {
      referenceTypeId: referenceTypeIdA,
      node: { modelId: componentIdA },
    }: DependencyMapReference,
    {
      referenceTypeId: referenceTypeIdB,
      node: { modelId: componentIdB },
    }: DependencyMapReference
  ) =>
    (referenceTypeIdA &&
      referenceTypeIdB &&
      localeCompareNumericLowercase(
        referenceTypeNames.get(referenceTypeIdA) ?? '',
        referenceTypeNames.get(referenceTypeIdB) ?? '',
        locale
      )) ||
    compareComponentIds(
      componentIdA,
      componentIdB,
      sort,
      locale,
      scopeData,
      workspaceIds
    );

const isReference = (
  value: DependencyMapReference | DependencyMapReferenceParent
): value is DependencyMapReference => 'referenceTypeId' in value;

const getUnknownReferenceComparator =
  (
    compareReferences: (
      a: DependencyMapReference,
      b: DependencyMapReference
    ) => number,
    sort: ContextSort,
    locale: Locale,
    scopeData: EnhancedScopeData,
    workspaceIds: ArdoqId[]
  ) =>
  (
    a: DependencyMapReference | DependencyMapReferenceParent,
    b: DependencyMapReference | DependencyMapReferenceParent
  ) =>
    isReference(a)
      ? isReference(b)
        ? compareReferences(a, b)
        : compareComponentIds(
            a.node.modelId,
            b.modelId,
            sort,
            locale,
            scopeData,
            workspaceIds
          )
      : isReference(b)
        ? compareComponentIds(
            a.modelId,
            b.node.modelId,
            sort,
            locale,
            scopeData,
            workspaceIds
          )
        : compareComponentIds(
            a.modelId,
            b.modelId,
            sort,
            locale,
            scopeData,
            workspaceIds
          );

const getReferenceComparator = (
  referenceTypeNames: Map<string, string>,
  sort: ContextSort,
  locale: Locale,
  scopeData: EnhancedScopeData,
  workspaceIds: ArdoqId[]
) => {
  const compareReferences = getSimpleReferenceComparator(
    referenceTypeNames,
    sort,
    locale,
    scopeData,
    workspaceIds
  );
  const compareUnknownReferences = getUnknownReferenceComparator(
    compareReferences,
    sort,
    locale,
    scopeData,
    workspaceIds
  );
  return (a: DependencyMapReference, b: DependencyMapReference) =>
    compareUnknownReferences(rootParent(a), rootParent(b));
};

export { getReferenceComparator };
